import * as React from 'react';
import { Fragment } from 'react';
import { compose, withHandlers, withProps } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { TableColumn } from 'logic/reports/ReportSettings';
import { ReportType } from 'types';
import { Select } from 'ui/shared/Select';
import { getTextKeyForColumn } from 'logic/reports/getTextKeyForColumn';
import { getColumnSortType } from 'logic/reports/reportType';

export type OwnProps = {
    reportType: ReportType;
    sortIndex: number;
    selectedColumn: string | null;
    ascending: boolean;
    columns: ReadonlyArray<TableColumn>;
    onColumnChanged: (index: number, columnName: string | null) => void;
    onDirectionChanged: (index: number, ascending: boolean) => void;
};

const RawColumnSortControl = ({
    reportType,
    sortIndex,
    columns,
    selectedColumn,
    ascending,
    sortDirections,
    handleColumnChange,
    handleDirectionChange,
    t,
}: OwnProps & TranslateProps & RecomposeProps) =>
    <Fragment>
        <label
            htmlFor={ `column-sort-control-${sortIndex}` }
        >
            { t(sortIndex === 0 ? 'WC_REPORT_COLUMNS_SORTBY' : 'WC_REPORT_COLUMNS_THENBY') }
        </label>
        <Select
            id={ `column-sort-control-${sortIndex}` }
            value={ selectedColumn || '' }
            onChange={ handleColumnChange }
        >
            <option value="">{ t('6.5.0.25772.11') }</option>
            { columns.map(g =>
                <option key={ g.name } value={ g.name }>
                    { t(getTextKeyForColumn(reportType, g.name)) }
                </option>
            ) }
        </Select>
        { !!selectedColumn &&
            <Select
                id={ `column-sort-control-${sortIndex}-direction` }
                value={ ascending ? 'ASC' : 'DESC' }
                onChange={ handleDirectionChange }
            >
                { sortDirections.map(({ value, label }) =>
                    <option key={ label } value={ value }>
                        { label }
                    </option>
                ) }
            </Select>
        }
    </Fragment>
;

type RecomposeProps = {
    sortDirections: ReadonlyArray<{ value: string; label: string }>;
    handleColumnChange: React.ChangeEventHandler<HTMLSelectElement>;
    handleDirectionChange: React.ChangeEventHandler<HTMLSelectElement>;
};

const makeSortDirectionOptions = (reportType: ReportType, columnName: string | null, t: TranslateProps['t']) => {
    const sortType = getColumnSortType(reportType, columnName);
    if (!sortType) {
        return []; // Cannot sort a thumbnail column, for example
    }
    return (
        [ 'ASC', 'DESC' ]
            .map((sortDir, i) => ({
                value: sortDir,
                label: t(`WC_REPORT_SORT_${sortType}_${sortDir}`),
            }))
    );
};

const filterEligibleColumns = (columns: ReadonlyArray<TableColumn>, sortIndex: number) =>
    columns
        .filter(c => typeof c.sortIndex !== 'number' || c.sortIndex >= sortIndex)
        .filter(c => typeof c.visibleIndex === 'number')
        .sort((a, b) => a.visibleIndex! - b.visibleIndex!)
;

export const ColumnSortControl = compose<RecomposeProps & TranslateProps & OwnProps, OwnProps>(
    translate(),
    withProps<Partial<RecomposeProps>, OwnProps & TranslateProps>(({ reportType, selectedColumn, sortIndex, columns, t }) => ({
        sortDirections: makeSortDirectionOptions(reportType, selectedColumn, t),
        columns: filterEligibleColumns(columns, sortIndex),
    })),
    withHandlers<OwnProps & RecomposeProps, Partial<RecomposeProps>>({
        handleColumnChange: ({ sortIndex, onColumnChanged }) => (e: React.ChangeEvent<HTMLSelectElement>) => {
            const columnName = e.currentTarget.value;
            onColumnChanged(sortIndex, columnName ? columnName : null);
        },
        handleDirectionChange: ({ sortIndex, onDirectionChanged }) => (e: React.ChangeEvent<HTMLSelectElement>) => {
            const ascending = e.currentTarget.value === 'ASC';
            onDirectionChanged(sortIndex, ascending);
        },
    }),
)(RawColumnSortControl);



// WEBPACK FOOTER //
// ./src/ui/pages/designer/sections/tableLayout/ColumnSortControl.tsx